import React, { useCallback, useEffect, useRef, useState } from 'react';

import './scss/privacy-policy.scss';

/**
 * Supported language codes for the Privacy Policy page.
 */
export type Language = 'en' | 'ar';

/**
 * Type definition for all textual content and translations of the Privacy Policy.
 */
export interface TranslationContent {
  header: {
    products: string;
    solutions: string;
    weAreIn: string;
    resources: string;
    aboutCompany: string;
    contactUs: string;
    downloadCatalogue: string;
    search: string;
  };
  hero: {
    badge: string;
    title: string;
    subtitle1: string;
    subtitle2: string;
  };
  section1: {
    title: string;
    intro: string;
    items: string[];
    usageIntro: string;
    usageItems: string[];
    thirdPartyNote: string;
    thirdPartyHeading: string;
    thirdPartyIntro: string;
    thirdPartyItems: string[];
  };
  section2: {
    title: string;
    intro: string;
    includeText: string;
    items: string[];
  };
  section3: {
    title: string;
    intro: string;
    helpText: string;
    items: string[];
    disclaimer: string;
  };
  section4: {
    title: string;
    intro: string;
    items: string[];
    disclaimer: string;
  };
  section5: {
    title: string;
    para1: string;
    para2: string;
  };
  section6: {
    title: string;
    para1: string;
    para2: string;
  };
  section7: {
    title: string;
    para1: string;
    para2: string;
    para3: string;
  };
  section8: {
    title: string;
    para1: string;
    para2: string;
    para3: string;
  };
  section9: {
    title: string;
    intro: string;
    emailLabel: string;
    chipLabel: string;
  };
  legalNav: {
    privacyPolicy: string;
    termsAndConditions: string;
    termsOfService: string;
    deleteAccount: string;
  };
}

/**
 * Bilingual content dictionary for English and Arabic.
 */
export const translations: Record<Language, TranslationContent> = {
  en: {
    header: {
      products: 'Products',
      solutions: 'Solutions',
      weAreIn: 'We Are In',
      resources: 'Resources',
      aboutCompany: 'About Company',
      contactUs: 'Contact Us',
      downloadCatalogue: 'Download Catalogue',
      search: 'Search',
    },
    hero: {
      badge: 'Legal & Privacy',
      title: 'Privacy Policy',
      subtitle1:
        'This Privacy Policy explains how Solutions Valley (وادي الحلول) collects, uses, stores, and protects your personal information when you use our website, mobile applications, and related services.',
      subtitle2:
        'By using our services, you agree to the collection and use of information in accordance with this Privacy Policy. Unless otherwise defined in this Privacy Policy, terms used here have the same meanings as those in our Terms and Conditions.',
    },
    section1: {
      title: 'Information Collection and Use',
      intro:
        'To provide and improve our services, we may request certain personally identifiable information, including but not limited to:',
      items: [
        'Full Name',
        'Email Address',
        'Mobile Number',
        'Company Information (where applicable)',
        'Charging Station Usage Information',
        'Device Information',
      ],
      usageIntro: 'The information collected is used to:',
      usageItems: [
        'Provide and maintain our services',
        'Improve user experience',
        'Respond to customer inquiries',
        'Process account-related requests',
        'Monitor platform performance',
        'Enhance security',
      ],
      thirdPartyNote:
        'Some third-party services used by our applications may also collect information necessary to provide their services.',
      thirdPartyHeading: 'Third-Party Services',
      thirdPartyIntro: 'Our applications may use the following third-party services:',
      thirdPartyItems: ['Google Play Services'],
    },
    section2: {
      title: 'Log Data',
      intro:
        'Whenever you use our services, especially in the event of an application error, we may collect Log Data through third-party services.',
      includeText: 'This information may include:',
      items: [
        'IP Address',
        'Device Name',
        'Device Model',
        'Operating System Version',
        'App Configuration',
        'Time and Date of Usage',
        'Diagnostic Information',
        'Crash Reports',
        'Performance Statistics',
      ],
    },
    section3: {
      title: 'Cookies',
      intro:
        'Our website may use cookies and similar technologies to improve your browsing experience.',
      helpText: 'Cookies help us:',
      items: [
        'Remember preferences',
        'Improve website functionality',
        'Analyze website traffic',
        'Enhance overall user experience',
      ],
      disclaimer:
        'You may choose to accept or decline cookies through your browser settings. Disabling cookies may affect certain features of our services.',
    },
    section4: {
      title: 'Service Providers',
      intro: 'We may engage trusted third-party companies and individuals to:',
      items: [
        'Operate our services',
        'Provide technical support',
        'Analyze service usage',
        'Improve platform performance',
      ],
      disclaimer:
        'These providers may have access to your personal information only for performing services on our behalf and are contractually obligated to protect your information.',
    },
    section5: {
      title: 'Data Security',
      para1:
        'We value your trust and use commercially reasonable administrative, technical, and physical safeguards to protect your personal information.',
      para2:
        'However, no method of internet transmission or electronic storage is completely secure, and we cannot guarantee absolute security.',
    },
    section6: {
      title: 'Links to Other Websites',
      para1:
        'Our services may contain links to external websites that are not operated by Solutions Valley.',
      para2:
        'We are not responsible for the privacy practices or content of third-party websites and encourage users to review their respective privacy policies.',
    },
    section7: {
      title: "Children's Privacy",
      para1: 'Our services are not intended for children under the age of 13.',
      para2: 'We do not knowingly collect personal information from children under 13 years of age.',
      para3:
        'If we become aware that such information has been collected, we will promptly delete it.',
    },
    section8: {
      title: 'Changes to This Privacy Policy',
      para1: 'We may update this Privacy Policy periodically.',
      para2:
        'Any changes will be published on this page and become effective immediately upon posting.',
      para3:
        'Users are encouraged to review this Privacy Policy regularly to stay informed about how we protect their information.',
    },
    section9: {
      title: 'Contact Us',
      intro:
        'If you have any questions, concerns, or suggestions regarding this Privacy Policy, please contact us:',
      emailLabel: 'Email:',
      chipLabel: 'Email Contact',
    },
    legalNav: {
      privacyPolicy: 'Privacy Policy',
      termsAndConditions: 'Terms & Conditions',
      termsOfService: 'Terms of Service',
      deleteAccount: 'Delete Account',
    },
  },
  ar: {
    header: {
      products: 'المنتجات',
      solutions: 'الحلول',
      weAreIn: 'مواقعنا',
      resources: 'الموارد',
      aboutCompany: 'عن الشركة',
      contactUs: 'اتصل بنا',
      downloadCatalogue: 'تحميل الكتالوج',
      search: 'بحث',
    },
    hero: {
      badge: 'الشؤون القانونية والخصوصية',
      title: 'سياسة الخصوصية',
      subtitle1:
        'توضح سياسة الخصوصية هذه كيف تقوم شركة وادي الحلول (Solutions Valley) بجمع معلوماتك الشخصية واستخدامها وتخزينها وحمايتها عند استخدامك لموقعنا الإلكتروني وتطبيقات الهاتف المحمول والخدمات ذات الصلة.',
      subtitle2:
        'باستخدامك لخدماتنا، فإنك توافق على جمع المعلومات واستخدامها وفقًا لسياسة الخصوصية هذه. ما لم يتم تحديد خلاف ذلك في سياسة الخصوصية هذه، فإن المصطلحات المستخدمة هنا لها نفس المعاني المنصوص عليها في الشروط والأحكام الخاصة بنا.',
    },
    section1: {
      title: 'جمع المعلومات واستخدامها',
      intro:
        'من أجل تقديم خدماتنا وتطويرها باستمرار، قد نطلب بعض معلومات التعريف الشخصية، بما في ذلك على سبيل المثال لا الحصر:',
      items: [
        'الاسم الكامل',
        'عنوان البريد الإلكتروني',
        'رقم الجوال',
        'معلومات الشركة (حيثما ينطبق ذلك)',
        'معلومات استخدام محطة الشحن',
        'معلومات الجهاز والتطبيق',
      ],
      usageIntro: 'تُستخدم المعلومات التي يتم جمعها في الأغراض التالية:',
      usageItems: [
        'تقديم خدماتنا وصيانتها بكفاءة عالية',
        'تحسين تجربة المستخدم وجودة التفاعل',
        'الرد السريع على استفسارات العملاء',
        'معالجة الطلبات وإدارة العمليات المتعلقة بالحساب',
        'مراقبة أداء واستقرار المنصة الرقمية',
        'تعزيز معايير الأمان وحماية البيانات',
      ],
      thirdPartyNote:
        'قد تقوم بعض خدمات الأطراف الثالثة المعتمدة في تطبيقاتنا بجمع المعلومات الضرورية لتمكين تشغيل خدماتها.',
      thirdPartyHeading: 'خدمات الطرف الثالث',
      thirdPartyIntro: 'قد تستخدم تطبيقاتنا التقنية خدمات الطرف الثالث التالية:',
      thirdPartyItems: ['خدمات جوجل بلاي (Google Play Services)'],
    },
    section2: {
      title: 'بيانات السجل (Log Data)',
      intro:
        'كلما استخدمت خدماتنا، لا سيما في حال حدوث خطأ أو توقف في التطبيق، فإننا قد نجمع بيانات السجل عبر خدمات موثوقة تابعة لجهات خارجية.',
      includeText: 'قد تتضمن هذه المعلومات ما يلي:',
      items: [
        'عنوان بروتوكول الإنترنت (IP Address)',
        'اسم الجهاز والمعرف الرقمي',
        'طراز الجهاز ومواصفاته',
        'إصدار نظام التشغيل',
        'تهيئة وإعدادات التطبيق',
        'وقت وتاريخ الاستخدام الدقيق',
        'معلومات التشخيص الفني',
        'تقارير وتحليلات الأعطال',
        'إحصائيات ومؤشرات الأداء',
      ],
    },
    section3: {
      title: 'ملفات تعريف الارتباط (الكوكيز)',
      intro:
        'قد يستخدم موقعنا الإلكتروني ملفات تعريف الارتباط والتقنيات التقنية المماثلة لتحسين تجربة التصفح وتخصيصها.',
      helpText: 'تساعدنا ملفات تعريف الارتباط في:',
      items: [
        'تذكر التفضيلات والخيارات المخصصة',
        'تحسين كفاءة ووظائف الموقع الإلكتروني',
        'تحليل حركة المرور وإحصائيات الزوار',
        'الارتقاء بتجربة المستخدم الشاملة',
      ],
      disclaimer:
        'يمكنك اختيار قبول أو رفض ملفات تعريف الارتباط من خلال إعدادات المتصفح لديك. يُرجى ملاحظة أن تعطيل ملفات تعريف الارتباط قد يؤثر على توفر بعض ميزات وخصائص خدماتنا.',
    },
    section4: {
      title: 'مقدمو الخدمات الخارجيون',
      intro: 'يجوز لنا الاستعانة بشركات وأفراد مؤهلين وموثوقين من أطراف ثالثة لأداء المهام التالية:',
      items: [
        'تشغيل وتطوير منظومة خدماتنا',
        'تقديم الدعم الفني والتقني المستمر',
        'تحليل وتحسين أنماط استخدام الخدمات',
        'رفع كفاءة وموثوقية أداء المنصة',
      ],
      disclaimer:
        'يُتاح لهؤلاء المزودين الوصول إلى معلوماتك الشخصية فقط لأداء المهام الموكلة إليهم نيابة عنا، وهم ملزمون تعاقديًا وقانونيًا بحماية سريتها وعدم استخدامها لأي غرض آخر.',
    },
    section5: {
      title: 'أمان وحماية البيانات',
      para1:
        'نحن نثمن ثقتكم الغالية ونطبق تدابير وقائية إدارية وفنية ومادية تتوافق مع أرقى المعايير لحماية معلوماتكم الشخصية من الوصول غير المصرح به.',
      para2:
        'ومع ذلك، يُرجى العلم بأنه لا توجد وسيلة نقل عبر الإنترنت أو وسيلة تخزين إلكترونية آمنة بنسبة 100٪، وبالتالي لا يمكننا ضمان الأمان المطلق والشامل.',
    },
    section6: {
      title: 'الروابط إلى المواقع الخارجية',
      para1:
        'قد تحتوي خدماتنا على روابط تؤدي إلى مواقع خارجية لا تخضع لإدارة أو تشغيل وادي الحلول.',
      para2:
        'نحن لسنا مسؤولين عن ممارسات الخصوصية أو السياسات أو المحتوى المتاح على تلك المواقع الخارجية، ونحث جميع المستخدمين على مراجعة سياسات الخصوصية الخاصة بها بشكل مستقل.',
    },
    section7: {
      title: 'خصوصية الأطفال والقُصّر',
      para1: 'خدماتنا غير موجهة للأطفال الذين تقل أعمارهم عن 13 عامًا.',
      para2: 'نحن لا نجمع عن علم أو قصد أي معلومات تعريف شخصية من الأطفال دون سن 13 عامًا.',
      para3:
        'في حال علمنا بجمع أي معلومات شخصية تخص طفلاً دون سن 13 عامًا دون موافقة الوالدين، فإننا نتخذ تدابير فورية لحذف تلك المعلومات من خوادمنا بشكل كامل.',
    },
    section8: {
      title: 'التعديلات على سياسة الخصوصية',
      para1: 'قد نقوم بمراجعة وتحديث سياسة الخصوصية هذه بصورة دورية بما يتناسب مع تطور خدماتنا والأنظمة المعمول بها.',
      para2:
        'سيتم نشر أي تعديلات جديدة مباشرة على هذه الصفحة وتعتبر نافذة المفعول فور نشرها.',
      para3:
        'ننصح المستخدمين بمراجعة سياسة الخصوصية هذه بانتظام للبقاء على اطلاع كامل بكيفية التزامنا الدائم بحماية معلوماتهم.',
    },
    section9: {
      title: 'اتصل بنا',
      intro:
        'إذا كانت لديكم أي استفسارات أو ملاحظات أو مقترحات بشأن سياسة الخصوصية هذه، يُسعدنا تواصلكم معنا عبر:',
      emailLabel: 'البريد الإلكتروني:',
      chipLabel: 'التواصل عبر البريد',
    },
    legalNav: {
      privacyPolicy: 'سياسة الخصوصية',
      termsAndConditions: 'الشروط والأحكام',
      termsOfService: 'شروط الخدمة',
      deleteAccount: 'حذف الحساب',
    },
  },
};

/**
 * Properties for the PrivacyPolicy component.
 */
export interface PrivacyPageProperties {
  showHeader?: boolean;
  initialLanguage?: Language;
  onLanguageChange?: (language: Language) => void;
}

interface IconProperties {
  size?: number | string;
  className?: string;
  color?: string;
}

/**
 * Renders the ShieldCheck SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function ShieldCheck(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

ShieldCheck.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the FileText SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function FileText(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  );
}

FileText.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Server SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function Server(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
      <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
      <line x1="6" x2="6.01" y1="6" y2="6" />
      <line x1="6" x2="6.01" y1="18" y2="18" />
    </svg>
  );
}

Server.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Cookie SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function Cookie(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
      <path d="M8.5 8.5v.01" />
      <path d="M16 15.5v.01" />
      <path d="M12 12v.01" />
      <path d="M11 17v.01" />
      <path d="M7 14v.01" />
    </svg>
  );
}

Cookie.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Users SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function Users(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

Users.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Lock SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function Lock(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

Lock.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the ExternalLink SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function ExternalLink(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

ExternalLink.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the ShieldAlert SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function ShieldAlert(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );
}

ShieldAlert.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the RefreshCw SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function RefreshCw(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

RefreshCw.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Mail SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function Mail(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

Mail.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the UserX SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function UserX(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="17" x2="22" y1="8" y2="13" />
      <line x1="22" x2="17" y1="8" y2="13" />
    </svg>
  );
}

UserX.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Search SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function Search(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

Search.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Globe SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function Globe(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

Globe.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the ChevronDown SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function ChevronDown(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

ChevronDown.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Menu SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function MenuIcon(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>
  );
}

MenuIcon.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Close SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function CloseIcon(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

CloseIcon.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

/**
 * Renders the Download SVG icon.
 * @param {IconProperties} properties - SVG icon properties.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
function DownloadIcon(properties: Readonly<IconProperties>): React.JSX.Element {
  const { size = 20, color = 'currentColor', className } = properties;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

DownloadIcon.defaultProps = {
  size: 20,
  color: 'currentColor',
  className: '',
};

interface HeaderProperties {
  language: Language;
  onLanguageChange: (selectedLanguage: Language) => void;
  onNavigate: (sectionIdentifier: string) => void;
}

/**
 * Integrated top navigation header for Solutions Valley.
 * @param {HeaderProperties} properties - Header component properties.
 * @returns {React.JSX.Element} The rendered Header element.
 */
function Header(properties: Readonly<HeaderProperties>): React.JSX.Element {
  const { language, onLanguageChange, onNavigate } = properties;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const headerReference = useRef<HTMLElement>(null);

  const headerContent = translations[language].header;

  /**
   * Handles selection of a new language and closes the dropdown menu.
   * @param {Language} selectedLanguage - The selected language code.
   * @returns {void}
   */
  function handleSelectLanguage(selectedLanguage: Language): void {
    onLanguageChange(selectedLanguage);
    setLanguageMenuOpen(false);
  }

  useEffect(() => {
    /**
     * Handles clicks outside the header to close the language dropdown.
     * @param {MouseEvent} mouseEvent - The browser mouse event.
     * @returns {void}
     */
    function handleOutsideClick(mouseEvent: MouseEvent): void {
      if (
        headerReference.current &&
        !headerReference.current.contains(mouseEvent.target as Node)
      ) {
        setLanguageMenuOpen(false);
      }
    }

    /**
     * Handles keyboard events for closing menus on Escape key.
     * @param {KeyboardEvent} keyboardEvent - The browser keyboard event.
     * @returns {void}
     */
    function handleKeyDown(keyboardEvent: KeyboardEvent): void {
      if (keyboardEvent.key === 'Escape') {
        setLanguageMenuOpen(false);
        setMobileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <header
      className={`site-header ${language === 'ar' ? 'is-rtl' : ''}`}
      ref={headerReference}>
      <div className="header-inner">
        {/* Brand Logo */}
        <a
          href="/"
          className="logo-brand"
          onClick={(mouseEvent) => {
            mouseEvent.preventDefault();
            onNavigate('top');
          }}
          aria-label="Solutions Valley - Home">
          <img
            src="https://energic-prod-bucket-new.s3.ap-south-1.amazonaws.com/tenants/sv/logo-sv1.png"
            alt="Solutions Valley - وادي الحلول"
            className="brand-logo-img"
          />
        </a>

        {/* Desktop Navigation Links */}
        {/* <nav className="desktop-nav" aria-label="Main Navigation">
          <a href="#products" className="nav-item">
            {headerContent.products}
          </a>
          <a href="#solutions" className="nav-item">
            {headerContent.solutions}
          </a>
          <div className="nav-item">
            <span>{headerContent.weAreIn}</span>
            <ChevronDown size={14} className="dropdown-arrow" />
          </div>
          <a href="#resources" className="nav-item">
            {headerContent.resources}
          </a>
          <a href="#about" className="nav-item">
            {headerContent.aboutCompany}
          </a>
          <a
            href="#contact"
            className="nav-item"
            onClick={(mouseEvent) => {
              mouseEvent.preventDefault();
              onNavigate('contact');
            }}>
            {headerContent.contactUs}
          </a>
        </nav> */}

        {/* Header Actions */}
        <div className="header-actions">
          {/* <button
            type="button"
            className="icon-button"
            aria-label={headerContent.search}
            title={headerContent.search}>
            <Search size={18} />
          </button> */}

          {/* Language Switcher */}
          <div className="lang-dropdown-wrapper">
            <button
              type="button"
              className="lang-button"
              onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
              aria-label="Select language">
              <Globe size={16} />
              <span>{language.toUpperCase()}</span>
              <ChevronDown size={13} />
            </button>
            {languageMenuOpen && (
              <div className="lang-menu">
                <button
                  type="button"
                  onClick={() => handleSelectLanguage('en')}
                  className={language === 'en' ? 'active' : ''}>
                  English (EN)
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectLanguage('ar')}
                  className={language === 'ar' ? 'active' : ''}>
                  العربية (AR)
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            type="button"
            className="mobile-toggle-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Menu">
            {mobileMenuOpen ? <CloseIcon size={24} /> : <MenuIcon size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-drawer">
          <nav className="mobile-nav">
            <a href="#products" onClick={() => setMobileMenuOpen(false)}>
              {headerContent.products}
            </a>
            <a href="#solutions" onClick={() => setMobileMenuOpen(false)}>
              {headerContent.solutions}
            </a>
            <a href="#we-are-in" onClick={() => setMobileMenuOpen(false)}>
              {headerContent.weAreIn}
            </a>
            <a href="#resources" onClick={() => setMobileMenuOpen(false)}>
              {headerContent.resources}
            </a>
            <a href="#about" onClick={() => setMobileMenuOpen(false)}>
              {headerContent.aboutCompany}
            </a>
            <a
              href="#contact"
              onClick={(mouseEvent) => {
                mouseEvent.preventDefault();
                setMobileMenuOpen(false);
                onNavigate('contact');
              }}>
              {headerContent.contactUs}
            </a>
            <a
              href="#catalogue"
              className="mobile-download-button"
              onClick={() => setMobileMenuOpen(false)}>
              <DownloadIcon size={16} />
              <span>{headerContent.downloadCatalogue}</span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

/**
 * Main Privacy Policy page component displaying policies, procedures, and contact info.
 * @param {PrivacyPageProperties} properties - Page properties.
 * @returns {React.JSX.Element} The rendered Privacy Policy page.
 */
function PrivacyPolicy(properties: Readonly<PrivacyPageProperties>): React.JSX.Element {
  const { showHeader = true, initialLanguage, onLanguageChange } = properties;

  const [language, setLanguage] = useState<Language>(() => {
    if (initialLanguage) {
      return initialLanguage;
    }
    const savedLanguage = localStorage.getItem('app_lang');
    if (savedLanguage === 'ar' || savedLanguage === 'en') {
      return savedLanguage;
    }
    return 'en';
  });

  /**
   * Handles language change and persists preference.
   * @param {Language} newLanguage - Selected language.
   * @returns {void}
   */
  const handleLanguageChange = useCallback(
    (newLanguage: Language): void => {
      setLanguage(newLanguage);
      localStorage.setItem('app_lang', newLanguage);
      if (onLanguageChange) {
        onLanguageChange(newLanguage);
      }
    },
    [onLanguageChange]
  );

  /**
   * Handles smooth navigation to in-page anchors or scrolls to the top of the page.
   * @param {string} sectionIdentifier - Identifier of the section to scroll to.
   * @returns {void}
   */
  const handleNavigate = useCallback((sectionIdentifier: string): void => {
    if (sectionIdentifier === 'contact') {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.hash = 'contact';
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    let timer: number | undefined;
    if (window.location.hash === '#contact') {
      timer = window.setTimeout(() => {
        const contactElement = document.getElementById('contact');
        if (contactElement) {
          contactElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const content = translations[language];

  return (
    <div
      className={`pageWrapper ${language === 'ar' ? 'is-rtl' : 'is-ltr'}`}
      dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Top Header */}
      {showHeader && (
        <Header
          language={language}
          onLanguageChange={handleLanguageChange}
          onNavigate={handleNavigate}
        />
      )}

      {/* Main Privacy Policy Content */}
      <main className={`mainContent ${!showHeader ? 'without-header' : ''}`}>
        {/* Hero Section */}
        <section className="heroSection" aria-labelledby="hero-heading">
          <div className="heroBadge">
            <ShieldCheck size={14} aria-hidden="true" />
            <span>{content.hero.badge}</span>
          </div>
          <h1 id="hero-heading" className="heroTitle">
            {content.hero.title}
          </h1>
          <div className="heroSubtitle">
            <p className="paragraph">{content.hero.subtitle1}</p>
            <p className="paragraph">{content.hero.subtitle2}</p>
          </div>
        </section>

        {/* Section 1: Information Collection and Use */}
        <section className="sectionCard" aria-labelledby="info-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <FileText size={22} />
            </div>
            <h2 id="info-heading" className="sectionTitle">
              {content.section1.title}
            </h2>
          </div>

          <p className="paragraph">{content.section1.intro}</p>

          <ul className="list">
            {content.section1.items.map((item, index) => (
              <li className="listItem" key={index}>
                <span className="listBullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>

          <p className="paragraph">{content.section1.usageIntro}</p>

          <ul className="list">
            {content.section1.usageItems.map((item, index) => (
              <li className="listItem" key={index}>
                <span className="listBullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>

          <p className="paragraph">{content.section1.thirdPartyNote}</p>

          <h3 className="subHeading">{content.section1.thirdPartyHeading}</h3>

          <p className="paragraph">{content.section1.thirdPartyIntro}</p>

          <ul className="list">
            {content.section1.thirdPartyItems.map((item, index) => (
              <li className="listItem" key={index}>
                <span className="listBullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Section 2: Log Data */}
        <section className="sectionCard" aria-labelledby="log-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <Server size={22} />
            </div>
            <h2 id="log-heading" className="sectionTitle">
              {content.section2.title}
            </h2>
          </div>

          <p className="paragraph">{content.section2.intro}</p>
          <p className="paragraph">{content.section2.includeText}</p>

          <ul className="list">
            {content.section2.items.map((item, index) => (
              <li className="listItem" key={index}>
                <span className="listBullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Section 3: Cookies */}
        <section className="sectionCard" aria-labelledby="cookies-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <Cookie size={22} />
            </div>
            <h2 id="cookies-heading" className="sectionTitle">
              {content.section3.title}
            </h2>
          </div>

          <p className="paragraph">{content.section3.intro}</p>
          <p className="paragraph">{content.section3.helpText}</p>

          <ul className="list">
            {content.section3.items.map((item, index) => (
              <li className="listItem" key={index}>
                <span className="listBullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>

          <p className="paragraph">{content.section3.disclaimer}</p>
        </section>

        {/* Section 4: Service Providers */}
        <section className="sectionCard" aria-labelledby="providers-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <Users size={22} />
            </div>
            <h2 id="providers-heading" className="sectionTitle">
              {content.section4.title}
            </h2>
          </div>

          <p className="paragraph">{content.section4.intro}</p>

          <ul className="list">
            {content.section4.items.map((item, index) => (
              <li className="listItem" key={index}>
                <span className="listBullet" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>

          <p className="paragraph">{content.section4.disclaimer}</p>
        </section>

        {/* Section 5: Data Security */}
        <section className="sectionCard" aria-labelledby="security-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <Lock size={22} />
            </div>
            <h2 id="security-heading" className="sectionTitle">
              {content.section5.title}
            </h2>
          </div>

          <p className="paragraph">{content.section5.para1}</p>
          <p className="paragraph">{content.section5.para2}</p>
        </section>

        {/* Section 6: Links to Other Websites */}
        <section className="sectionCard" aria-labelledby="links-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <ExternalLink size={22} />
            </div>
            <h2 id="links-heading" className="sectionTitle">
              {content.section6.title}
            </h2>
          </div>

          <p className="paragraph">{content.section6.para1}</p>
          <p className="paragraph">{content.section6.para2}</p>
        </section>

        {/* Section 7: Children's Privacy */}
        <section className="sectionCard" aria-labelledby="children-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <ShieldAlert size={22} />
            </div>
            <h2 id="children-heading" className="sectionTitle">
              {content.section7.title}
            </h2>
          </div>

          <p className="paragraph">{content.section7.para1}</p>
          <p className="paragraph">{content.section7.para2}</p>
          <p className="paragraph">{content.section7.para3}</p>
        </section>

        {/* Section 8: Changes to This Privacy Policy */}
        <section className="sectionCard" aria-labelledby="changes-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <RefreshCw size={22} />
            </div>
            <h2 id="changes-heading" className="sectionTitle">
              {content.section8.title}
            </h2>
          </div>

          <p className="paragraph">{content.section8.para1}</p>
          <p className="paragraph">{content.section8.para2}</p>
          <p className="paragraph">{content.section8.para3}</p>
        </section>

        {/* Section 9: Contact Us */}
        <section
          id="contact"
          className="sectionCard"
          aria-labelledby="contact-heading">
          <div className="sectionHeader">
            <div className="sectionHeaderIcon" aria-hidden="true">
              <Mail size={22} />
            </div>
            <h2 id="contact-heading" className="sectionTitle">
              {content.section9.title}
            </h2>
          </div>

          <p className="paragraph">{content.section9.intro}</p>

          <p className="paragraph">
            {content.section9.emailLabel}
            <br />
            <a href="mailto:info@se.com.sa" className="link">
              info@se.com.sa
            </a>
          </p>

          <div>
            <a
              href="mailto:info@se.com.sa"
              className="contactChip"
              aria-label="Email info@se.com.sa">
              <div className="chipIcon" aria-hidden="true">
                <Mail size={18} />
              </div>
              <div>
                <div className="chipLabel">{content.section9.chipLabel}</div>
                <div className="chipValue">info@se.com.sa</div>
              </div>
            </a>
          </div>
        </section>

        {/* Legal Navigation Bar */}
        <nav className="privacyBar" aria-label="Legal document links">
          <a
            href="/privacy-policy"
            className="privacyLink active"
            aria-label={content.legalNav.privacyPolicy}>
            <ShieldCheck size={16} aria-hidden="true" />
            <span>{content.legalNav.privacyPolicy}</span>
          </a>
          <a
            href="#terms-of-service"
            className="privacyLink"
            aria-label={content.legalNav.termsOfService}>
            <ExternalLink size={16} aria-hidden="true" />
            <span>{content.legalNav.termsOfService}</span>
          </a>
          <a
            href="#delete-account"
            className="privacyLink"
            aria-label={content.legalNav.deleteAccount}>
            <UserX size={16} aria-hidden="true" />
            <span>{content.legalNav.deleteAccount}</span>
          </a>
        </nav>
      </main>
    </div>
  );
}

PrivacyPolicy.defaultProps = {
  showHeader: true,
  initialLanguage: undefined,
  onLanguageChange: undefined,
};

export default PrivacyPolicy;
